import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Step1BasicInfo from "../components/partner-join/Step1BasicInfo";
import Step2CategoryLocation from "../components/partner-join/Step2CategoryLocation";
import Step3ManagementContact from "../components/partner-join/Step3ManagementContact";
import Step4WasteInfo from "../components/partner-join/Step4WasteInfo";
import Step5ReviewSubmit from "../components/partner-join/Step5ReviewSubmit";
import SuccessModal from "../components/partner-join/SuccessModal";

import OrganizationService from "../services/ApiService";
import logoPilari from "../assets/images/pilari_light.png";
import logoGZW from "../assets/images/gzw.png";

const PartnerJoinPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    website: "",
    category: "",
    type: "",
    province: "",
    city: "",
    district: "",
    subdistrict: "",
    fullAddress: "",
    region: "",
    latitude: "",
    longitude: "",
    acceptedWaste: "",
    openingHours: "",
    incentiveSystem: "",
    wasteManagementSystem: "",
    legalStatus: "",
    legalNumber: "",
    description: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    picName: "",
    picPosition: "",
    picPhone: "",
    picEmail: "",
    transactionSystem: "",
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);

  const isSpecialType = ["partner", "waste_bank"].includes(formData.type);
  const steps = [
    "Step1",
    "Step2",
    ...(isSpecialType ? ["Step3", "Step4"] : []),
    "Review",
  ];
  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = steps[stepIndex];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await OrganizationService.getCategories();
        setCategoryOptions(categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchTypes = async () => {
      if (!formData.category) {
        setTypeOptions([]);
        return;
      }
      try {
        const types = await OrganizationService.getTypesByCategory(
          formData.category
        );
        setTypeOptions(types);
      } catch (error) {
        console.error("Error fetching types:", error);
        setTypeOptions([]);
      }
    };
    fetchTypes();
  }, [formData.category]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category" ? { type: "" } : {}),
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateStep = (stepKey) => {
    const newErrors = {};
    if (stepKey === "Step1") {
      if (!formData.name) newErrors.name = t("partnerJoin.errors.name");
      if (!formData.email) newErrors.email = t("partnerJoin.errors.email");
      if (!formData.phone) newErrors.phone = t("partnerJoin.errors.phone");
    }
    if (stepKey === "Step2") {
      if (!formData.category)
        newErrors.category = t("partnerJoin.errors.category");
      if (!formData.type) newErrors.type = t("partnerJoin.errors.type");
      if (!formData.address)
        newErrors.address = t("partnerJoin.errors.address");
    }
    if (stepKey === "Step3") {
      if (!formData.picName)
        newErrors.picName = t("partnerJoin.errors.picName");
      if (!formData.picPhone)
        newErrors.picPhone = t("partnerJoin.errors.picPhone");
    }
    if (stepKey === "Step4") {
      if (!formData.acceptedWaste)
        newErrors.acceptedWaste = t("partnerJoin.errors.acceptedWaste");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setStepIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => setStepIndex((prev) => prev - 1);
  const handleSubmit = () => setShowSuccess(true);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-300 py-24 px-4 font-sans">
      <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden transition-colors duration-300">
        {/* Hero Section */}
        <div className="relative h-[320px] sm:h-[400px]">
          <img
            src={require("../assets/images/cikuray.webp")}
            alt="Gunung Cikuray"
            className="absolute inset-0 w-full h-full object-cover brightness-75"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6">
            <img
              src={logoPilari}
              alt="Logo Pilari"
              className="w-20 sm:w-24 mb-3 drop-shadow-md"
            />
            <h1 className="text-3xl sm:text-4xl font-bold drop-shadow-lg">
              {t("partnerJoin.title")}
            </h1>
            <p className="text-sm sm:text-base font-medium drop-shadow-sm mt-2">
              {t("partnerJoin.subtitle")}
            </p>
          </div>
        </div>

        {/* Form Section */}
        <div className="p-8 space-y-6">
          {currentStep === "Step1" && (
            <Step1BasicInfo
              formData={formData}
              onChange={handleChange}
              errors={errors}
            />
          )}

          {currentStep === "Step2" && (
            <Step2CategoryLocation
              formData={formData}
              onChange={handleChange}
              categoryOptions={categoryOptions}
              typeOptions={typeOptions}
              errors={errors}
            />
          )}

          {currentStep === "Step3" && (
            <Step3ManagementContact
              formData={formData}
              onChange={handleChange}
              errors={errors}
            />
          )}

          {currentStep === "Step4" && (
            <Step4WasteInfo
              formData={formData}
              onChange={handleChange}
              errors={errors}
            />
          )}

          {currentStep === "Review" && (
            <Step5ReviewSubmit
              formData={formData}
              onSubmit={handleSubmit}
              loading={false}
            />
          )}

          {/* Navigasi Button */}
          <div className="flex justify-between pt-4">
            {stepIndex > 0 && (
              <button
                onClick={handleBack}
                className="px-6 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {t("partnerJoin.buttons.back")}
              </button>
            )}

            {stepIndex < steps.length - 1 && (
              <button
                onClick={handleNext}
                className="ml-auto px-6 py-2 bg-green-700 hover:bg-green-800 text-white rounded-lg transition"
              >
                {t("partnerJoin.buttons.next")}
              </button>
            )}
          </div>
        </div>

        {/* Footer Section */}
        <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 text-center py-4">
          <div className="inline-flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{t("partnerJoin.poweredBy")}</span>
            <img src={logoGZW} alt="Garut Zero Waste" className="h-8" />
          </div>
        </footer>
      </div>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
    </div>
  );
};

export default PartnerJoinPage;
